import { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// ── Keyframes
const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50%       { opacity: 0.9; transform: scale(1.06); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const floatUp = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
`;

// ── Section
const Section = styled.section`
  position: relative;
  background: #050008;
  overflow: hidden;
  padding: 140px 0 160px;
  id: about;
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || "110px"});
  pointer-events: none;
  opacity: ${({ op }) => op || 0.18};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  animation: ${pulse} ${({ dur }) => dur || "5s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
`;

// Diagonal decorative lines
const DiagLine = styled.div`
  position: absolute;
  width: 1px;
  height: 220px;
  background: linear-gradient(
    180deg,
    transparent,
    rgba(168, 85, 247, 0.25),
    transparent
  );
  transform: rotate(${({ angle }) => angle || "25deg"});
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  pointer-events: none;
  z-index: 0;
`;

const Container = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 clamp(1.5rem, 5vw, 4rem);
`;

// ── Top label row
const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2.5rem;
  opacity: 0;
  transform: translateX(-30px);
`;

const Label = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #a855f7;
`;

const LabelLine = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(168, 85, 247, 0.4), transparent);
`;

// ── Main layout: left text, right visual
const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(3rem, 6vw, 7rem);
  align-items: center;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

// ── Left: text
const TextCol = styled.div``;

const BigStatement = styled.h2`
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(2.6rem, 5.5vw, 5rem);
  font-weight: 300;
  line-height: 1.12;
  color: #fff;
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(40px);

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }
`;

const BodyText = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: clamp(0.9rem, 1.4vw, 1.05rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.42);
  line-height: 1.9;
  margin-bottom: 1.4rem;
  opacity: 0;
  transform: translateY(25px);
`;

// CTA button
const CtaWrap = styled.div`
  opacity: 0;
  transform: translateY(20px);
`;

const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 28px;
  height: 52px;
  border-radius: 100px;
  background: transparent;
  border: 1px solid rgba(168, 85, 247, 0.45);
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition:
    border-color 0.3s ease,
    color 0.3s ease,
    box-shadow 0.3s ease;

  /* shimmer sweep on hover */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(168, 85, 247, 0.15),
      rgba(232, 121, 249, 0.1)
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(232, 121, 249, 0.7);
    color: #fff;
    box-shadow:
      0 0 30px rgba(168, 85, 247, 0.25),
      0 0 60px rgba(168, 85, 247, 0.1);
    &::before {
      opacity: 1;
    }
  }

  &:hover .arrow {
    transform: translateX(4px);
  }

  .arrow {
    display: inline-block;
    transition: transform 0.25s ease;
    font-style: normal;
  }
`;

// ── Right: visual cards stack
const VisualCol = styled.div`
  position: relative;
  height: 400px;
  opacity: 0;
  transform: translateX(40px);

  @media (max-width: 768px) {
    height: 280px;
  }
`;

const FloatingCard = styled.div`
  position: absolute;
  border-radius: ${({ r }) => r || "20px"};
  border: 1px solid rgba(168, 85, 247, 0.2);
  overflow: hidden;
  animation: ${floatUp} ${({ dur }) => dur || "4s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

// Card 1 — big mission card
const MissionCard = styled(FloatingCard)`
  top: 0;
  left: 0;
  width: 70%;
  padding: 1.6rem;
  background: linear-gradient(
    145deg,
    rgba(40, 10, 70, 0.9),
    rgba(20, 5, 40, 0.95)
  );
`;

const MissionLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 0.7rem;
`;

const MissionText = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.25rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
`;

// Card 2 — year / founded
const FoundedCard = styled(FloatingCard)`
  bottom: 40px;
  right: 0;
  width: 44%;
  padding: 1.4rem;
  background: linear-gradient(
    145deg,
    rgba(124, 58, 237, 0.2),
    rgba(10, 0, 20, 0.95)
  );
  text-align: center;
`;

const FoundedNum = styled.div`
  font-family: "Cormorant Garamond", serif;
  font-size: 3rem;
  font-weight: 600;
  color: #fff;
  line-height: 1;
  margin-bottom: 0.3rem;
`;

const FoundedLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
`;

// Card 3 — small accent tag
const AccentTag = styled(FloatingCard)`
  top: 48%;
  right: 5%;
  padding: 0.7rem 1.1rem;
  background: rgba(232, 121, 249, 0.12);
  border-color: rgba(232, 121, 249, 0.3);
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 100px;
`;

const AccentDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e879f9;
  box-shadow: 0 0 10px #e879f9;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const AccentText = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
`;

// ── Bottom values strip
const ValuesStrip = styled.div`
  display: flex;
  gap: clamp(1.5rem, 3vw, 3rem);
  flex-wrap: wrap;
  border-top: 1px solid rgba(168, 85, 247, 0.1);
  padding-top: 3rem;
  opacity: 0;
  transform: translateY(30px);
`;

const Value = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
  min-width: 180px;
`;

const ValueIcon = styled.div`
  font-size: 1.3rem;
  flex-shrink: 0;
  margin-top: 2px;
`;

const ValueText = styled.div``;

const ValueTitle = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 0.25rem;
  letter-spacing: 0.02em;
`;

const ValueDesc = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.28);
  line-height: 1.6;
`;

// ── Data
const VALUES = [
  {
    icon: "🔥",
    title: "Radical Honesty",
    desc: "No fake profiles. No illusions. Just real people showing up as themselves.",
  },
  {
    icon: "🔒",
    title: "Privacy by Design",
    desc: "Security isn't bolted on — it's built into every decision from day one.",
  },
  {
    icon: "⚡",
    title: "Chemistry First",
    desc: "We believe attraction is instant. Our app is built around that truth.",
  },
  {
    icon: "🌍",
    title: "Built for Everyone",
    desc: "Regardless of who you are or where you're from — you belong here.",
  },
];

export default function AboutSection() {
  const sectionRef = useRef(null);
  const labelRef = useRef(null);
  const headlineRef = useRef(null);
  const body1Ref = useRef(null);
  const body2Ref = useRef(null);
  const ctaRef = useRef(null);
  const visualRef = useRef(null);
  const valuesRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Label slides in from left
      gsap
        .timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 78%",
            end: "top 45%",
            scrub: 0.7,
          },
        })
        .to(labelRef.current, { opacity: 1, x: 0, ease: "power2.out" });

      // Headline + body cascade
      gsap
        .timeline({
          scrollTrigger: {
            trigger: headlineRef.current,
            start: "top 85%",
            end: "top 45%",
            scrub: 0.7,
          },
        })
        .to(headlineRef.current, { opacity: 1, y: 0, ease: "power3.out" }, 0)
        .to(body1Ref.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.15)
        .to(body2Ref.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.28)
        .to(ctaRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.4);

      // Visual col slides from right
      gsap
        .timeline({
          scrollTrigger: {
            trigger: visualRef.current,
            start: "top 82%",
            end: "top 45%",
            scrub: 0.8,
          },
        })
        .to(visualRef.current, { opacity: 1, x: 0, ease: "power3.out" });

      // Values strip
      gsap
        .timeline({
          scrollTrigger: {
            trigger: valuesRef.current,
            start: "top 88%",
            end: "top 55%",
            scrub: 0.7,
          },
        })
        .to(valuesRef.current, { opacity: 1, y: 0, ease: "power2.out" });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef} id="about">
      <Grain />
      <Orb
        s="500px"
        c="rgba(124,58,237,0.2)"
        top="10%"
        left="-8%"
        op={0.22}
        dur="6s"
        del="0s"
        blur="120px"
      />
      <Orb
        s="380px"
        c="rgba(232,121,249,0.15)"
        bottom="5%"
        right="-5%"
        op={0.18}
        dur="7s"
        del="1.5s"
        blur="110px"
      />
      <DiagLine angle="20deg" top="10%" left="12%" />
      <DiagLine angle="-15deg" top="40%" right="8%" />
      <DiagLine angle="30deg" bottom="15%" left="55%" />

      <Container>
        {/* Label */}
        <LabelRow ref={labelRef}>
          <Label>About Us</Label>
          <LabelLine />
        </LabelRow>

        {/* Main layout */}
        <Layout>
          <TextCol>
            <BigStatement ref={headlineRef}>
              We built the app
              <br />
              we <em>wished</em> existed.
            </BigStatement>

            <BodyText ref={body1Ref}>
              We were tired of apps that gamified love, hid real people behind
              algorithms, and treated your most personal data like a product. So
              we started over — from a blank page and a single conviction:
              attraction is human, and technology should respect that.
            </BodyText>

            <BodyText ref={body2Ref}>
              Today we're a small, obsessive team that believes great
              connections start with honesty — about who you are, what you want,
              and the fact that chemistry can't be engineered. It can only be
              discovered.
            </BodyText>

            <CtaWrap ref={ctaRef}>
              <CtaButton href="/about">
                Our full story
                <span className="arrow">→</span>
              </CtaButton>
            </CtaWrap>
          </TextCol>

          {/* Visual */}
          <VisualCol ref={visualRef}>
            <MissionCard dur="5s" del="0s">
              <MissionLabel>Our mission</MissionLabel>
              <MissionText>
                "Make attraction honest,
                <br />
                and connection inevitable."
              </MissionText>
            </MissionCard>

            <FoundedCard dur="6s" del="0.8s">
              <FoundedNum>2026</FoundedNum>
              <FoundedLabel>Founded</FoundedLabel>
            </FoundedCard>

            <AccentTag dur="4.5s" del="0.4s">
              <AccentDot />
              <AccentText>Real people only</AccentText>
            </AccentTag>
          </VisualCol>
        </Layout>

        {/* Values */}
        <ValuesStrip ref={valuesRef}>
          {VALUES.map((v, i) => (
            <Value key={i}>
              <ValueIcon>{v.icon}</ValueIcon>
              <ValueText>
                <ValueTitle>{v.title}</ValueTitle>
                <ValueDesc>{v.desc}</ValueDesc>
              </ValueText>
            </Value>
          ))}
        </ValuesStrip>
      </Container>
    </Section>
  );
}
